import { useCallback, useMemo } from "react";
import { View } from "react-native";
import { LinearTransition } from "react-native-reanimated";
import { FRATIFY_ME, PHOTO_DETAIL_RECAP, REACTIONS } from "src/api/featureFlags";
import GatheringPrivacy from "src/api/graphql/__generated__/enums/GatheringPrivacy";
import PhotoState from "src/api/graphql/__generated__/enums/PhotoState";
import PhotoVariant from "src/api/graphql/__generated__/enums/PhotoVariant";
import OpacityView from "src/components/OpacityView";
import PhotoInfoBlurbDisplay from "src/components/PhotoInfoBlurbDisplay";
import {
	DefaultShareComponentLookup,
	SharePayloadProp,
	ShareWrapper,
	ValidShareOption,
} from "src/components/ShareMenu";
import { useGatheringStore } from "src/contexts/GatheringContext";
import useDevice from "src/hooks/useDevice";
import { PhotoItem } from "src/screens/(internal)/PhotoDetailModal/types";
import { paddingSmallSpacer, paddingXXXL } from "src/styles/spacing";
import PhotoDetailReactionsButton from "../../../../PhotoDetailReactionsButton";
import PhotoDetailFratifyMeButton from "./PhotoDetailFratifyMeButton";

const PhotoDetailFooterContent = ({
	photo,
	imageShareProps,
	linkShareProps,
}: {
	photo: PhotoItem | null;
	imageShareProps: SharePayloadProp | null;
	linkShareProps: SharePayloadProp | null;
}) => {
	const hasAltContent = useMemo(
		() =>
			Boolean(
				photo?.state === PhotoState.Hidden ||
					photo?.state === PhotoState.Preparing ||
					photo?.state === PhotoState.Errored,
			),
		[photo?.state],
	);
	const privacy = useGatheringStore((state) => state.privacy);
	const hasFratify = useMemo(
		() =>
			FRATIFY_ME &&
			photo?.variant !== PhotoVariant.Video &&
			!privacy?.includes(GatheringPrivacy.DisableGenAi),
		[photo?.variant, privacy],
	);
	// Reactions usually overlays on top of the photo
	// However, videos have a UI (for full screen and such) which means we have to move reactions to the footer
	const hasReactionsButton = useMemo(
		() => REACTIONS && !!photo && photo.variant === PhotoVariant.Video,
		[photo],
	);

	const renderOptions = useCallback((options: ValidShareOption[]) => {
		const Content = options.map((option) => {
			const Component = DefaultShareComponentLookup[option.key];
			return (
				<OpacityView
					key={option.key}
					webLayoutId={option.key}
					nativeLayout={LinearTransition}
				>
					<Component key={option.key} data={option.data as any} />
				</OpacityView>
			);
		});
		return Content;
	}, []);

	const renderImageItems = useCallback(
		(options: ValidShareOption[]) => {
			const filteredOptions = options
				.filter(
					(option) =>
						option.key === "ShareDownload" ||
						(!PHOTO_DETAIL_RECAP &&
							!hasFratify &&
							option.key === "ShareInstagramStory"),
				)
				.slice(0, hasReactionsButton ? 1 : 2);
			return renderOptions(filteredOptions);
		},
		[hasReactionsButton, renderOptions, hasFratify],
	);
	const renderLinkItems = useCallback(
		(options: ValidShareOption[]) => {
			const filteredOptions: ValidShareOption[] = options
				.filter((option) => option.key === "ShareShare")
				.slice(0, 1);

			// fallback on Clipboard if no native share
			if (!filteredOptions.find((option) => option.key === "ShareShare")) {
				const shareClipboard = options.find((option) => option.key === "ShareClipboard");
				if (shareClipboard) filteredOptions.push(shareClipboard);
			}
			return renderOptions(filteredOptions);
		},
		[renderOptions],
	);
	const { device } = useDevice();

	return (
		<View
			testID="ShareMenu"
			style={[
				{
					flexDirection: "row",
					alignItems: "center",
					width: "100%",
					paddingHorizontal: paddingXXXL,
				},
				device === "mobile" && hasReactionsButton
					? {
							justifyContent: "space-between",
						}
					: {
							justifyContent: "center",
							columnGap: paddingSmallSpacer,
						},
			]}
		>
			{hasAltContent ? (
				<>
					{!!photo && (
						<OpacityView key={photo.state}>
							<PhotoInfoBlurbDisplay photo={photo} />
						</OpacityView>
					)}
				</>
			) : (
				<>
					{imageShareProps && (
						<ShareWrapper
							{...imageShareProps}
							displayIconText={false}
							renderItems={renderImageItems}
						/>
					)}
					{hasReactionsButton && !!photo && (
						<OpacityView
							webLayoutId="PhotoDetailReactionsButton"
							nativeLayout={LinearTransition}
							exiting={false}
						>
							<PhotoDetailReactionsButton photo={photo} maxNumProfilePictures={1} />
						</OpacityView>
					)}
					{hasFratify && <PhotoDetailFratifyMeButton />}
					{linkShareProps && (
						<ShareWrapper
							{...linkShareProps}
							displayIconText={false}
							renderItems={renderLinkItems}
						/>
					)}
				</>
			)}
		</View>
	);
};
export default PhotoDetailFooterContent;
